// ========== DOMAIN / SETUP ==========

export interface PushDomainConfig {
    id: number;
    domain_id: number;
    mongo_domain_id: string | null;
    vapid_public_key: string | null;
    trigger_mode: 'auto' | 'manual';
    created_at: string;
    updated_at: string;
}

export interface PushDomain {
    id: number;
    domain: string;
    purpose: 'normal' | 'capture';
    is_active: number;
    created_at: string;
    updated_at: string;
    push_config: PushDomainConfig | null;
}

export interface PushDomainSetupResponse {
    domain: PushDomain;
    config: PushDomainConfig;
    created: boolean;
    message: string;
}

// ========== CAMPAIGNS ==========

export interface PushCampaign {
    id: number;
    domain_id: number;
    group_id: number | null;
    name: string;
    type: 'direct' | 'scheduled' | 'recurring';
    status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';
    title: string;
    body: string;
    url: string;
    icon: string | null;
    image: string | null;
    badge: string | null;
    actions: any[] | null;
    require_interaction: boolean;
    utm_campaign: string | null;
    utm_medium: string | null;
    utm_source: string | null;
    tag_on_receive: string | null;
    tag_on_click: string | null;
    segment_countries: string[] | null;
    segment_states: string[] | null;
    segment_cities: string[] | null;
    segment_tags: string[] | null;
    segment_has_name: boolean;
    segment_has_email: boolean;
    segment_clicked_campaign: number | null;
    segment_not_clicked_campaign: number | null;
    scheduled_at: string | null;
    recurring_cron: string | null;
    recurring_until: string | null;
    total_sent: number;
    total_delivered: number;
    total_clicks: number;
    total_failed: number;
    ctr: number;
    sent_at: string | null;
    cancelled_at: string | null;
    generated_by_ai: boolean;
    translate_enabled: boolean;
    translate_mode: 'all' | 'specific' | null;
    translate_languages: string[] | null;
    source_language: string;
    translate_url_mode: 'none' | 'path' | 'subdomain';
    created_at: string;
    updated_at: string;
}

export interface PushCampaignCreate {
    domain_id?: number;
    group_id?: number;
    name: string;
    type: 'direct' | 'scheduled' | 'recurring';
    title: string;
    body: string;
    url: string;
    icon?: string;
    image?: string;
    badge?: string;
    actions?: any[];
    require_interaction?: boolean;
    utm_campaign?: string;
    utm_medium?: string;
    utm_source?: string;
    tag_on_receive?: string;
    tag_on_click?: string;
    segment_countries?: string[];
    segment_states?: string[];
    segment_cities?: string[];
    segment_tags?: string[];
    segment_has_name?: boolean;
    segment_has_email?: boolean;
    segment_clicked_campaign?: number;
    segment_not_clicked_campaign?: number;
    scheduled_at?: string;
    recurring_cron?: string;
    recurring_until?: string;
    generated_by_ai?: boolean;
    translate_enabled?: boolean;
    translate_mode?: 'all' | 'specific';
    translate_languages?: string[];
    source_language?: string;
    translate_url_mode?: 'none' | 'path' | 'subdomain';
}

export interface PushCampaignUpdate {
    name?: string;
    title?: string;
    body?: string;
    url?: string;
    icon?: string;
    image?: string;
    badge?: string;
    actions?: any[];
    require_interaction?: boolean;
    utm_campaign?: string;
    utm_medium?: string;
    utm_source?: string;
    tag_on_receive?: string;
    tag_on_click?: string;
    segment_countries?: string[];
    segment_states?: string[];
    segment_cities?: string[];
    segment_tags?: string[];
    segment_has_name?: boolean;
    segment_has_email?: boolean;
    scheduled_at?: string;
    recurring_cron?: string;
    recurring_until?: string;
    status?: string;
    translate_enabled?: boolean;
    translate_mode?: 'all' | 'specific';
    translate_languages?: string[];
    source_language?: string;
    translate_url_mode?: 'none' | 'path' | 'subdomain';
}

export interface PushCampaignPaginated {
    items: PushCampaign[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

// ========== FLOWS ==========

export interface PushFlowNode {
    id: number;
    flow_id: number;
    type: 'notification' | 'delay' | 'condition' | 'tag';
    position: number;
    parent_node_id: number | null;
    branch: 'yes' | 'no' | null;
    title: string | null;
    body: string | null;
    url: string | null;
    icon: string | null;
    image: string | null;
    badge: string | null;
    actions: any[] | null;
    require_interaction: boolean;
    utm_campaign: string | null;
    tag_on_receive: string | null;
    tag_on_click: string | null;
    delay_minutes: number | null;
    condition_type: 'clicked' | 'not_clicked' | null;
    condition_node_id: number | null;
    condition_wait_minutes: number | null;
    tag_action: 'add' | 'remove' | null;
    tag_name: string | null;
    total_entered: number;
    total_sent: number;
    total_clicks: number;
    created_at: string;
    updated_at: string;
}

export interface PushFlow {
    id: number;
    domain_id: number;
    group_id: number | null;
    name: string;
    description: string | null;
    status: 'active' | 'paused' | 'draft';
    trigger_type: 'new_lead' | 'tag_added';
    trigger_tag: string | null;
    segment_countries: string[] | null;
    segment_states: string[] | null;
    segment_cities: string[] | null;
    segment_tags: string[] | null;
    utm_source: string | null;
    utm_medium: string | null;
    total_entered: number;
    total_completed: number;
    total_clicks: number;
    translate_enabled: boolean;
    translate_mode: 'all' | 'specific' | null;
    translate_languages: string[] | null;
    source_language: string;
    translate_url_mode: 'none' | 'path' | 'subdomain';
    created_at: string;
    updated_at: string;
    nodes?: PushFlowNode[];
}

export interface PushFlowCreate {
    domain_id?: number;
    group_id?: number;
    name: string;
    description?: string;
    trigger_type: 'new_lead' | 'tag_added';
    trigger_tag?: string;
    segment_countries?: string[];
    segment_states?: string[];
    segment_cities?: string[];
    segment_tags?: string[];
    utm_source?: string;
    utm_medium?: string;
    translate_enabled?: boolean;
    translate_mode?: 'all' | 'specific';
    translate_languages?: string[];
    source_language?: string;
    translate_url_mode?: 'none' | 'path' | 'subdomain';
}

export interface PushFlowUpdate {
    name?: string;
    description?: string;
    status?: 'active' | 'paused' | 'draft';
    trigger_type?: 'new_lead' | 'tag_added';
    trigger_tag?: string;
    segment_countries?: string[];
    segment_states?: string[];
    segment_cities?: string[];
    segment_tags?: string[];
    utm_source?: string;
    utm_medium?: string;
    translate_enabled?: boolean;
    translate_mode?: 'all' | 'specific';
    translate_languages?: string[];
    source_language?: string;
    translate_url_mode?: 'none' | 'path' | 'subdomain';
}

export interface PushFlowNodeCreate {
    flow_id: number;
    type: 'notification' | 'delay' | 'condition' | 'tag';
    position?: number;
    parent_node_id?: number;
    branch?: 'yes' | 'no';
    title?: string;
    body?: string;
    url?: string;
    icon?: string;
    image?: string;
    badge?: string;
    actions?: any[];
    require_interaction?: boolean;
    utm_campaign?: string;
    tag_on_receive?: string;
    tag_on_click?: string;
    delay_minutes?: number;
    condition_type?: 'clicked' | 'not_clicked';
    condition_node_id?: number;
    condition_wait_minutes?: number;
    tag_action?: 'add' | 'remove';
    tag_name?: string;
}

export interface PushFlowNodeUpdate {
    position?: number;
    title?: string;
    body?: string;
    url?: string;
    icon?: string;
    image?: string;
    badge?: string;
    actions?: any[];
    utm_campaign?: string;
    tag_on_receive?: string;
    tag_on_click?: string;
    delay_minutes?: number;
    condition_type?: 'clicked' | 'not_clicked';
    condition_node_id?: number;
    condition_wait_minutes?: number;
    tag_action?: 'add' | 'remove';
    tag_name?: string;
}

export interface PushFlowPaginated {
    items: PushFlow[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export interface PushFlowNodeReport {
    id: number;
    type: string;
    position: number;
    title: string | null;
    total_entered: number;
    total_sent: number;
    total_clicks: number;
    ctr: number;
    utm_campaign: string | null;
    revenue: number;
    impressions: number;
    ecpm: number;
}

export interface PushFlowReport {
    flow: PushFlow;
    total_entered: number;
    total_completed: number;
    total_clicks: number;
    completion_rate: number;
    revenue?: {
        total_revenue: number;
        total_impressions: number;
        ecpm: number;
    };
    nodes: PushFlowNodeReport[];
}

// ========== LEADS ==========

export interface PushLead {
    totalReceived: number;
    _id: string;
    endpoint: string;
    keys: {
        p256dh: string;
        auth: string;
    };
    domainId: string;
    status: 'active' | 'unsubscribed' | 'failed' | 'expired';
    userAgent: string | null;
    ip: string | null;
    country: string;
    countryCode: string;
    state: string;
    stateCode: string;
    city: string;
    tags: string[];
    name: string | null;
    email: string | null;
    phone: string | null;
    totalClicks: number;
    lastClickedCampaignId: string | null;
    lastPushAt: string | null;
    failCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface PushLeadsPaginated {
    data: PushLead[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface PushLeadStats {
    total: number;
    active: number;
    unsubscribed: number;
    failed: number;
    expired: number;
}

// ========== DELIVERIES ==========

export type PushDeliveryKind = 'campaign' | 'flow';

export interface PushDelivery {
    _id: string;
    subscriptionId: string;
    campaignId: string;
    kind: PushDeliveryKind;
    domainId: string;
    projectId?: string | null;
    success: boolean;
    statusCode: number | null;
    error: string | null;
    sentAt: string;
    clickedAt: string | null;
    // Enrichment (opcional, vindo do CRUD)
    campaign_db_id?: number | null;
    campaign_name?: string | null;
    campaign_title?: string | null;
    campaign_url?: string | null;
    campaign_icon?: string | null;
    flow_name?: string | null;
    flow_node_id?: number | null;
    // Enriquecimento lead (em /campaigns/{id}/leads)
    lead_name?: string | null;
    lead_email?: string | null;
    lead_phone?: string | null;
    lead_country?: string | null;
    lead_country_code?: string | null;
    lead_city?: string | null;
    lead_tags?: string[];
    lead_status?: string | null;
}

export interface PushDeliveriesPaginated {
    data: PushDelivery[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface PushDeliveryByCampaign {
    campaignId: string;
    kind: PushDeliveryKind;
    total: number;
    delivered: number;
    failed: number;
    clicked: number;
    lastSentAt: string | null;
    ctr: number;
    // Enriquecimento
    campaign_db_id?: number | null;
    campaign_name?: string | null;
    campaign_title?: string | null;
    campaign_url?: string | null;
    campaign_icon?: string | null;
    flow_name?: string | null;
    flow_node_id?: number | null;
}

export type PushPeriodFilter = '1h' | '24h' | '7d' | '30d';

// ========== DASHBOARD ==========

export interface DashboardOverview {
    domain_id: number;
    domain: string;
    period: { start: string; end: string };
    campaigns: {
        total_campaigns: number;
        total_sent: number;
        total_delivered: number;
        total_clicks: number;
        total_failed: number;
        ctr: number;
    };
    flows: {
        total_flows: number;
        total_entered: number;
        total_completed: number;
        total_clicks: number;
        completion_rate: number;
    };
    revenue: {
        total_revenue: number;
        total_impressions: number;
        ecpm: number;
        campaigns_revenue: number;
        flows_revenue: number;
        by_date: { date: string; revenue: number; impressions: number; clicks: number }[];
        error?: string;
    };
}

export interface DashboardCampaignItem {
    id: number;
    name: string;
    title: string;
    body: string;
    url: string;
    icon: string | null;
    image: string | null;
    type: 'direct' | 'scheduled' | 'recurring';
    status: string;
    sent_at: string;
    scheduled_at: string;
    total_sent: number;
    total_delivered: number;
    total_clicks: number;
    total_failed: number;
    ctr: number;
    utm_campaign: string;
    revenue: number;
    impressions: number;
    ecpm: number;
}

export interface DashboardCampaignsReport {
    summary: {
        total_campaigns: number;
        total_sent: number;
        total_delivered: number;
        total_clicks: number;
        total_failed: number;
        ctr: number;
        total_revenue: number;
        total_impressions: number;
        ecpm: number;
    };
    items: DashboardCampaignItem[];
}

export interface DashboardFlowItem {
    id: number;
    name: string;
    description: string;
    status: string;
    trigger_type: string;
    trigger_tag: string | null;
    first_title: string;
    first_body: string;
    first_icon: string | null;
    total_entered: number;
    total_completed: number;
    total_clicks: number;
    completion_rate: number;
    total_nodes: number;
    revenue: number;
    impressions: number;
    ecpm: number;
}

export interface DashboardFlowsReport {
    summary: {
        total_flows: number;
        total_entered: number;
        total_completed: number;
        total_clicks: number;
        completion_rate: number;
        total_revenue: number;
        total_impressions: number;
        ecpm: number;
    };
    items: DashboardFlowItem[];
}

// ========== GROUPS ==========

export interface PushNotificationGroup {
    id: number;
    user_id: number;
    name: string;
    description: string | null;
    is_active: boolean;
    domain_count: number;
    created_at: string;
    updated_at: string;
    domains?: PushDomain[];
}

export interface PushGroupCreate {
    name: string;
    description?: string;
}

export interface PushGroupUpdate {
    name?: string;
    description?: string;
    is_active?: boolean;
}

// ========== COMMON ==========

export interface MessageResponse {
    message: string;
    success?: boolean;
}

// ========== CRON PRESETS ==========

export const CRON_PRESETS = [
    { label: 'Todo dia às 10h', value: '0 10 * * *' },
    { label: 'De 6 em 6 horas', value: '0 */6 * * *' },
    { label: 'Toda segunda às 9h', value: '0 9 * * 1' },
    { label: 'Quarta e sexta às 10h', value: '0 10 * * 3,5' },
    { label: 'Seg a sex às 14:30', value: '30 14 * * 1-5' },
    { label: 'Dia 1 de todo mês às 10h', value: '0 10 1 * *' },
    { label: 'Dia 5 de todo mês às 10h', value: '0 10 5 * *' },
    { label: 'Dia 1 e 15 de todo mês', value: '0 9 1,15 * *' },
    { label: 'A cada 2 minutos (teste)', value: '*/2 * * * *' },
];
// ========== TRANSLATIONS ==========

export interface TranslationLanguage {
    code: string;
    name: string;
}

export interface TranslationLanguagesResponse {
    languages: TranslationLanguage[];
}

export interface PushTag {
    id: number;
    domain_id: number;
    name: string;
    color: string;
    description: string | null;
    created_at: string;
    updated_at: string;
}